import React, { useState } from 'react';
// Importing shared constants from the global config file
import { MONTHS, YEARS } from '../constants/crmConstants';

/**
 * SettingsScreen Component
 * Manages Global Configuration and System Setup.
 * Features:
 * 1. Master Data Management (Lists) - Updated to link B2B Users to Source Agents
 * 2. University-Program mapping with Country assignment & Course list management.
 * 3. Intake Mapping (Program to Intake Dates using global constants).
 * 4. Document Checklist Templates (Auto-population for Student Pipeline).
 */
const SettingsScreen = ({ settings, users, botLogs = [], onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('master'); // 'master', 'universities', 'intakes', or 'documents'
  const [editingUni, setEditingUni] = useState(null);

  // Filter for only TWG B2B Users to be used in the dropdown
  const b2bUsers = (users || []).filter(u => u.role === 'TWG B2B User');

  /**
   * Helper to normalize source agent data.
   * Converts old string format ["Agent A"] into new object format [{name: "Agent A", userId: ""}]
   */
  const normalizeAgents = (agents) => {
    if (!Array.isArray(agents)) return [];
    return agents.map(agent => {
      if (typeof agent === 'string') {
        return { name: agent, userId: '' };
      }
      return agent;
    });
  };

  // Local state for Master Data with safe defaults and Normalization applied
  const [masterData, setMasterData] = useState({
    countries: settings?.countries || ['India', 'USA', 'UK', 'Canada', 'Australia'],
    qualifications: settings?.qualifications || ['High School / Class XII', 'Undergraduate Degree', 'Postgraduate Degree'],
    twgPrograms: settings?.twgPrograms || ['International Year One', 'Pre-Masters', 'Direct Admission'],
    sourceAgents: normalizeAgents(settings?.sourceAgents)
  });

  React.useEffect(() => {
    if (settings) {
      setMasterData({
        countries: settings.countries || ['India', 'USA', 'UK', 'Canada', 'Australia'],
        qualifications: settings.qualifications || ['High School / Class XII', 'Undergraduate Degree', 'Postgraduate Degree'],
        twgPrograms: settings.twgPrograms || ['International Year One', 'Pre-Masters', 'Direct Admission'],
        sourceAgents: normalizeAgents(settings.sourceAgents)
      });
    }
  }, [settings]);

  // NEW: Local state for Document Templates
  const [documentTemplates, setDocumentTemplates] = useState(settings?.documentTemplates || {});

  React.useEffect(() => {
    if (settings?.documentTemplates) {
      setDocumentTemplates(settings.documentTemplates);
    }
  }, [settings?.documentTemplates]);

  /**
   * Helper to normalize university data.
   */
  /**
 * Helper to normalize university data and migrate old course lists
 * to the new hierarchical Program -> Course mapping.
 */
  const normalizeUniversities = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((uni, idx) => {
      // 1. Handle legacy string format
      if (typeof uni === 'string') {
        return { 
          id: `uni-${Date.now()}-${idx}`, 
          name: uni, 
          country: '',
          applicablePrograms: [], 
          courses: {} 
        };
      }

      let structuredCourses = {};

      // 2. Data Migration Logic: 
      // Check if 'courses' is an old array.
      if (Array.isArray(uni.courses)) {
        // If there are courses and at least one program, map everything to the first program.
        if (uni.courses.length > 0 && uni.applicablePrograms?.length > 0) {
          const primaryProgram = uni.applicablePrograms[0];
          structuredCourses[primaryProgram] = uni.courses;
        } else {
          // Fallback for cases with no programs defined yet
          structuredCourses = {};
        }
      } else if (typeof uni.courses === 'object' && uni.courses !== null) {
        // Data is already in the new format.
        structuredCourses = uni.courses;
      }

      return {
        id: uni.id || `uni-${Date.now()}-${idx}`,
        name: uni.name || '',
        country: uni.country || '',
        applicablePrograms: uni.applicablePrograms || [],
        courses: structuredCourses
      };
    });
  };

  // Local state for Universities
  const [universities, setUniversities] = useState(
    settings?.universities?.length > 0 
      ? normalizeUniversities(settings.universities)
      : [
          { 
            id: '1', 
            name: 'University of York', 
            country: 'UK',
            applicablePrograms: ['International Year One'], 
            courses: ['Bachelors of Business', 'Bachelors of Management'] 
          }
        ]
  );

  // Local state for Intake Mappings
  const [intakeMappings, setIntakeMappings] = useState(settings?.intakeMappings || {});

  const handleSave = () => {
    // Filter out completely empty rows before saving
    const cleanUniversities = universities.filter(u => u.name.trim() !== '' || u.applicablePrograms.length > 0 || u.courses.length > 0);
    
    onUpdate({
      ...masterData,
      universities: cleanUniversities,
      intakeMappings,
      documentTemplates // Persisting the new checklist templates
    });
    onClose();
  };

  // University Logic
  const addUniversity = () => {
    const newUni = { 
      id: Date.now().toString(), 
      name: '', 
      country: '',
      applicablePrograms: [], 
      courses: [] 
    };
    setUniversities([...universities, newUni]);
    setEditingUni(newUni.id);
  };

  const updateUni = (id, field, value) => {
    setUniversities(prev => prev.map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  const deleteUni = (id) => {
    if (window.confirm('Are you sure you want to remove this university mapping?')) {
      setUniversities(prev => prev.filter(u => u.id !== id));
      if (editingUni === id) setEditingUni(null);
    }
  };

  const toggleProgram = (uniId, program) => {
    const uni = universities.find(u => u.id === uniId);
    if (!uni) return;
    const currentPrograms = uni.applicablePrograms || [];
    const programs = currentPrograms.includes(program)
      ? currentPrograms.filter(p => p !== program)
      : [...currentPrograms, program];
    updateUni(uniId, 'applicablePrograms', programs);
  };

  const addCourse = (uniId, program) => {
    const uni = universities.find(u => u.id === uniId);
    if (!uni) return;
    
    const currentCourses = uni.courses || {};
    const programCourses = currentCourses[program] || [];
    
    updateUni(uniId, 'courses', {
      ...currentCourses,
      [program]: [...programCourses, '']
    });
  };

  const updateCourse = (uniId, program, index, value) => {
    const uni = universities.find(u => u.id === uniId);
    if (!uni) return;
    
    const currentCourses = { ...uni.courses };
    const programCourses = [...(currentCourses[program] || [])];
    programCourses[index] = value;
    currentCourses[program] = programCourses;
    
    updateUni(uniId, 'courses', currentCourses);
  };

  const removeCourse = (uniId, program, index) => {
    const uni = universities.find(u => u.id === uniId);
    if (!uni) return;
    
    const currentCourses = { ...uni.courses };
    currentCourses[program] = (currentCourses[program] || []).filter((_, i) => i !== index);
    
    updateUni(uniId, 'courses', currentCourses);
  };

  // Intake Logic
  const addIntakeDate = (program) => {
    const currentIntakes = intakeMappings[program] || [];
    const defaultDate = `${MONTHS[0]} ${YEARS[0]}`; 
    setIntakeMappings({
      ...intakeMappings,
      [program]: [...currentIntakes, defaultDate]
    });
  };

  const updateIntakePart = (program, index, part, value) => {
    const currentIntakes = [...(intakeMappings[program] || [])];
    const existing = currentIntakes[index] || "";
    const [m, y] = existing.includes(" ") ? existing.split(" ") : [MONTHS[0], YEARS[0]];
    
    const newDate = part === 'month' ? `${value} ${y}` : `${m} ${value}`;
    currentIntakes[index] = newDate;
    
    setIntakeMappings({
      ...intakeMappings,
      [program]: currentIntakes
    });
  };

  const removeIntakeDate = (program, index) => {
    const currentIntakes = (intakeMappings[program] || []).filter((_, i) => i !== index);
    setIntakeMappings({
      ...intakeMappings,
      [program]: currentIntakes
    });
  };

  const renderUniForm = (uni) => (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden animate-in slide-in-from-right-4 duration-300">
      <div className="px-10 py-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setEditingUni(null)} className="p-2 text-slate-400 hover:text-[#0464AD] hover:bg-white rounded-lg transition-all">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h4 className="text-lg font-black text-slate-900 tracking-tight">{uni.name || 'New Registration'}</h4>
        </div>
        <button onClick={() => deleteUni(uni.id)} className="text-[10px] font-black text-red-400 uppercase tracking-widest">Delete Institution</button>
      </div>

      <div className="p-10 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left Column: Basic Details & Program Selection */}
          <div className="space-y-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">University Name</label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={uni.name} onChange={(e) => updateUni(uni.id, 'name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country</label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none" value={uni.country} onChange={(e) => updateUni(uni.id, 'country', e.target.value)}>
                <option value="">Select Country...</option>
                {masterData.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Applicable TWG Programs</label>
              <div className="flex flex-wrap gap-2">
                {masterData.twgPrograms.map(prog => (
                  <button 
                    key={prog} 
                    onClick={() => toggleProgram(uni.id, prog)} 
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${uni.applicablePrograms?.includes(prog) ? 'bg-[#0464AD] text-white border-[#0464AD]' : 'bg-white text-slate-400 border-slate-100'}`}
                  >
                    {prog}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Hierarchical Course Mapping */}
          <div className="space-y-6">
            <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest border-b pb-2">Program-Specific Courses</h4>
            {uni.applicablePrograms?.length > 0 ? (
              <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {uni.applicablePrograms.map(prog => (
                  <div key={prog} className="bg-slate-50 p-5 rounded-2xl border border-slate-100 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-black text-[#0464AD] uppercase tracking-tight">{prog}</span>
                      <button 
                        onClick={() => addCourse(uni.id, prog)} 
                        className="text-[9px] font-black bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:border-[#0464AD] transition-all"
                      >
                        + Add Course
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(uni.courses?.[prog] || []).map((course, idx) => (
                        <div key={idx} className="flex items-center gap-2 group">
                          <input 
                            type="text" 
                            className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-medium outline-none focus:ring-1 focus:ring-[#0464AD]" 
                            value={course} 
                            placeholder="Enter Course Name..."
                            onChange={(e) => updateCourse(uni.id, prog, idx, e.target.value)} 
                          />
                          <button 
                            onClick={() => removeCourse(uni.id, prog, idx)} 
                            className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                      {(!uni.courses?.[prog] || uni.courses[prog].length === 0) && (
                        <p className="text-[10px] text-slate-300 italic text-center py-2">No courses added for this program</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
                <p className="text-xs text-slate-400 font-medium">Select TWG Programs on the left to begin adding courses.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <button onClick={() => setEditingUni(null)} className="bg-slate-900 text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">Confirm Details</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-in fade-in duration-500 max-w-6xl mx-auto pb-20">
      <div className="flex justify-between items-center mb-10">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tighter uppercase leading-none">Global Configuration</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">System Architecture & Validation Rules</p>
        </div>
        <div className="flex gap-4">
          <button onClick={onClose} className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-slate-100">Cancel</button>
          <button onClick={handleSave} className="bg-[#0464AD] text-white px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-[#0091D0] active:scale-95 transition-all">Save Changes</button>
        </div>
      </div>

      {/* Tabs Row */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner border border-slate-200/50 mb-8 w-fit">
        {[
          { id: 'master', label: 'Master Lists' },
          { id: 'universities', label: 'University Mapping' },
          { id: 'intakes', label: 'Intake Mapping' },
          { id: 'documents', label: 'Checklists' },
          { id: 'bot_logs', label: 'Bot Intelligence Logs' }
        ].map((tab) => (
          <button 
            key={tab.id} 
            onClick={() => { setActiveTab(tab.id); setEditingUni(null); }}
            className={`px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-white text-[#0464AD] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: MASTER LISTS */}
      {activeTab === 'master' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          {Object.entries(masterData).map(([key, list]) => (
            <div key={key} className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8 h-fit">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6 border-b border-slate-50 pb-4">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
              <div className="space-y-4">
                {list.map((item, i) => (
                  <div key={i} className="space-y-2 p-3 bg-slate-50 rounded-xl border border-slate-100 group relative">
                    {key === 'sourceAgents' ? (
                      <>
                        <input 
                          type="text" 
                          placeholder="Agent Name"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-[#0464AD]" 
                          value={item.name || ''} 
                          onChange={(e) => {
                            const newList = [...list];
                            newList[i] = { ...newList[i], name: e.target.value };
                            setMasterData({ ...masterData, [key]: newList });
                          }} 
                        />

                        {/* New Field 1: Agent Code */}  
                        <div className="grid grid-cols-2 gap-2">
      
                          <input 
                            type="text" 
                            placeholder="Agent Code (e.g. AG001)"
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]" 
                            value={item.code || ''} 
                            onChange={(e) => {
                              const newList = [...list];
                              newList[i] = { ...newList[i], code: e.target.value };
                              setMasterData({ ...masterData, [key]: newList });
                            }} 
                          />
                          
                          {/* New Field 2: Agent Email ID */}
                          <input 
                            type="email" 
                            placeholder="Agent Email ID"
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]" 
                            value={item.email || ''} 
                            onChange={(e) => {
                              const newList = [...list];
                              newList[i] = { ...newList[i], email: e.target.value };
                              setMasterData({ ...masterData, [key]: newList });
                            }} 
                          />
                        </div>
                        
                        {/* B2B User Selector */}
                        <select 
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[10px] font-black uppercase outline-none cursor-pointer"
                          value={item.userId || ''}
                          onChange={(e) => {
                            const newList = [...list];
                            newList[i] = { ...newList[i], userId: e.target.value };
                            setMasterData({ ...masterData, [key]: newList });
                          }}
                        >
                          <option value="">Select B2B User...</option>
                          {b2bUsers.map(u => <option key={u.uid} value={u.uid}>{u.name}</option>)}
                        </select>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input 
                          type="text" 
                          className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-[#0464AD]" 
                          value={item} 
                          onChange={(e) => { 
                            const newList = [...list]; 
                            newList[i] = e.target.value; 
                            setMasterData({ ...masterData, [key]: newList }); 
                          }} 
                        />
                      </div>
                    )}
                    <button 
                      onClick={() => setMasterData({ ...masterData, [key]: list.filter((_, idx) => idx !== i) })} 
                      className="absolute -top-2 -right-2 p-1 bg-white border border-slate-200 text-slate-300 hover:text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button 
                  onClick={() => {
                    const newEntry = key === 'sourceAgents' ? { name: '', code: '', email: '', userId: '' } : '';
                    setMasterData({ ...masterData, [key]: [...list, newEntry] });
                  }} 
                  className="w-full py-2 text-[9px] font-black uppercase text-[#0464AD] hover:bg-blue-50 rounded-xl transition-all"
                >
                  + Add Entry
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 2: UNIVERSITY MAPPING */}
      {activeTab === 'universities' && (
        <div className="space-y-6">
          {editingUni ? renderUniForm(universities.find(u => u.id === editingUni)) : (
            <>
              <div className="flex justify-between items-center px-2">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Institution Registry</h3>
                <button onClick={addUniversity} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase hover:bg-slate-800 transition-all">+ New University</button>
              </div>
              <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">University Name</th>
                      <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Country</th>
                      <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {universities.map(uni => (
                      <tr key={uni.id} className="hover:bg-slate-50/50 transition-colors group">
                        <td className="px-8 py-5 text-sm font-bold text-slate-800">{uni.name || 'Unnamed Institution'}</td>
                        <td className="px-8 py-5 text-xs font-black text-slate-500 uppercase">{uni.country || 'N/A'}</td>
                        
                        <td className="px-8 py-5 text-right">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setEditingUni(uni.id)} className="p-2 text-slate-300 hover:text-[#0464AD] transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                            <button onClick={() => deleteUni(uni.id)} className="p-2 text-slate-300 hover:text-red-500 transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* TAB 3: INTAKE MAPPING */}
      {activeTab === 'intakes' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
          {masterData.twgPrograms.map(prog => (
            <div key={prog} className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8 flex flex-col">
              <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4"><h4 className="text-sm font-black text-slate-800 uppercase">{prog}</h4><button onClick={() => addIntakeDate(prog)} className="text-[9px] font-black text-[#0464AD] uppercase bg-blue-50 px-3 py-1.5 rounded-lg">+ Add</button></div>
              <div className="space-y-3">
                {(intakeMappings[prog] || []).map((dateStr, idx) => (
                  <div key={idx} className="flex gap-2">
                    <select className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold" value={dateStr.split(" ")[0]} onChange={(e) => updateIntakePart(prog, idx, 'month', e.target.value)}>{MONTHS.map(m => <option key={m} value={m}>{m}</option>)}</select>
                    <select className="flex-1 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold" value={dateStr.split(" ")[1]} onChange={(e) => updateIntakePart(prog, idx, 'year', e.target.value)}>{YEARS.map(y => <option key={y} value={y}>{y}</option>)}</select>
                    <button onClick={() => removeIntakeDate(prog, idx)} className="p-2 text-slate-300 hover:text-red-500">✕</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TAB 4: DOCUMENT CHECKLISTS */}
      {activeTab === 'documents' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in duration-500">
          <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm h-fit">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6 border-b border-slate-50 pb-4">Country Templates</h3>
            <div className="space-y-4">
              {masterData.countries.map(country => (
                <div key={country} className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">{country} Requirements</span>
                    <button 
                      onClick={() => setDocumentTemplates({ ...documentTemplates, [country]: [...(documentTemplates[country] || []), ''] })}
                      className="text-[9px] font-black text-[#0464AD] uppercase"
                    >
                      + Add Doc
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(documentTemplates[country] || []).map((doc, idx) => (
                      <div key={idx} className="flex items-center gap-2 group">
                        <input type="text" className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-2 text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]" value={doc} placeholder="e.g. Passport Copy" onChange={(e) => { const newDocs = [...documentTemplates[country]]; newDocs[idx] = e.target.value; setDocumentTemplates({ ...documentTemplates, [country]: newDocs }); }} />
                        <button onClick={() => { const newDocs = documentTemplates[country].filter((_, i) => i !== idx); setDocumentTemplates({ ...documentTemplates, [country]: newDocs }); }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100">✕</button>
                      </div>
                    ))}
                    {(!documentTemplates[country] || documentTemplates[country].length === 0) && <p className="text-[10px] text-slate-400 italic">No standard documents defined.</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-blue-50/50 p-8 rounded-[2.5rem] border border-blue-100 h-fit space-y-4">
            <h4 className="text-xs font-black text-[#0464AD] uppercase tracking-widest leading-none">Automatic Checklist Logic</h4>
            <p className="text-xs text-slate-600 leading-relaxed font-medium">Define templates here. When moving to <strong>"Enrolled in Hybrid"</strong>, student checklists are instantly populated based on the Destination Country.</p>
          </div>
        </div>
      )}

      {/* TAB 5: BOT INTELLIGENCE LOGS */}
      {activeTab === 'bot_logs' && (
        <div className="space-y-6 animate-in fade-in duration-500">
          <div className="flex justify-between items-end mb-4 px-2">
            <div>
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Interaction Audit Trail</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Reviewing {botLogs.length} recent AI queries</p>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Timestamp</th>
                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">User Details</th>
                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">User Query</th>
                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">AI Response</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {botLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-8 py-6 whitespace-nowrap">
                        <span className="text-[10px] font-bold text-slate-500">
                          {log.timestamp?.toDate().toLocaleString('en-GB', { 
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                          })}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-slate-800 uppercase tracking-tight">{log.userName}</span>
                          <span className="text-[9px] font-bold text-[#0464AD] uppercase">{log.userRole}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-xs font-medium text-slate-600 leading-relaxed italic line-clamp-2 hover:line-clamp-none transition-all">
                          "{log.query}"
                        </p>
                      </td>
                      <td className="px-8 py-6">
                        <div className="text-[11px] font-medium text-slate-500 line-clamp-2 hover:line-clamp-none transition-all cursor-default">
                          {log.response}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {botLogs.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-8 py-20 text-center text-slate-300 font-bold uppercase tracking-widest italic">
                        No assistant interactions recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;